import { useEffect, useId, useState } from "react";
import { ChoiceControl, Dialog } from "@carepass/ui";
import type { Bank, TerminationPreview } from "../shared/types";
import { Alert, Button, DetailList, Field, Surface, money, navigate } from "../shared/ui";
import { TerminationAmount } from "./TerminationView";

export interface TerminationRequest {
  quoteToken: string;
  reason: string;
  agreedToRefundPolicy: boolean;
  confirmed: boolean;
  bankCode?: string;
  accountNumber?: string;
  accountHolder?: string;
}

export function TerminationForm({ preview, banks, memberName, pending, error, accountStatus = "ready", onRetryAccount, quoteReview, confirmationRevision = 0, onRetryPreview, onSubmit }: {
  readonly preview: TerminationPreview;
  readonly banks: readonly Bank[];
  readonly memberName: string;
  readonly pending: boolean;
  readonly error?: string;
  readonly accountStatus?: "loading" | "error" | "ready";
  readonly onRetryAccount?: () => void;
  readonly quoteReview?: "refreshing" | "updated" | "error";
  readonly confirmationRevision?: number;
  readonly onRetryPreview?: () => void;
  readonly onSubmit: (request: TerminationRequest) => void;
}) {
  const [agreed, setAgreed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  useEffect(() => {
    setAgreed(false);
    setConfirming(false);
  }, [confirmationRevision, preview.quoteToken]);
  const guidanceId = useId();
  const checkingQuote = quoteReview === "refreshing" || quoteReview === "error";
  const needsAccount = preview.requiresBankAccount ?? (preview.bankRefundAmount ?? 0) > 0;
  const fullCancel = preview.allowed && preview.sameDayNoUsage === true && preview.method === "PG_CANCEL";
  const hasAmount = typeof preview.customerTotalRefundAmount === "number" && Number.isFinite(preview.customerTotalRefundAmount);
  const hasQuote = typeof preview.quoteToken === "string" && preview.quoteToken.length > 0;
  const accountReady = accountStatus === "ready" && banks.length > 0 && memberName.trim().length > 0;
  const ready = !checkingQuote && preview.allowed && hasAmount && hasQuote && agreed && (!needsAccount || (
    accountReady && banks.some(bank => bank.code === bankCode) && accountNumber.length >= 8 && accountNumber.length <= 20
  ));
  const label = fullCancel ? "해지하기 (월정액 100% 취소)" : needsAccount ? "해지하고 환불 신청" : "해지하기";
  const guidance = checkingQuote ? "최신 해지 조건과 금액을 확인한 후 신청할 수 있습니다."
    : !preview.allowed ? "진행 중인 거래를 확인한 후 다시 신청해 주세요."
    : !hasAmount ? "취소·환불 금액을 확인한 후 신청할 수 있습니다."
    : !hasQuote ? "최신 해지 조건을 다시 확인해 주세요."
    : !agreed ? "개인정보 삭제 및 이용제한 안내를 확인해 주세요."
    : needsAccount && !accountReady ? "환불 계좌에 필요한 은행과 회원 정보를 확인하고 있습니다."
    : needsAccount && !bankCode ? "환불받을 은행을 선택해 주세요."
    : needsAccount && (accountNumber.length < 8 || accountNumber.length > 20) ? "환불받을 계좌번호 8~20자리를 입력해 주세요." : undefined;

  return <div className="termination-stack">
    {preview.allowed && !hasQuote && !quoteReview && <Alert persistent variant="warning" title="최신 해지 조건을 다시 확인해 주세요"
      action={onRetryPreview && <Button variant="quiet" onClick={onRetryPreview}>금액 다시 확인하기</Button>}>해지 조건을 확인한 후 신청할 수 있습니다.</Alert>}
    {quoteReview && <Alert persistent variant="warning" title={quoteReview === "refreshing" ? "해지 조건을 다시 확인하고 있습니다" : quoteReview === "error" ? "최신 해지 조건을 확인하지 못했습니다" : "해지 조건이 변경되었습니다"}
      action={quoteReview === "error" && onRetryPreview ? <Button variant="quiet" onClick={onRetryPreview}>금액 다시 확인하기</Button> : undefined}>
      {quoteReview === "refreshing" ? "이용 일수와 취소·환불 금액을 불러오고 있습니다. 아직 해지가 접수되지 않았습니다."
        : quoteReview === "error" ? "현재 표시된 금액으로는 신청할 수 없습니다. 다시 확인해 주세요."
        : "새로 확인한 이용 일수와 취소·환불 금액을 확인하고 다시 동의해 주세요. 아직 해지가 접수되지 않았습니다."}
    </Alert>}
    <Alert persistent variant="danger" className="termination-policy-warning" title="해지 전 확인해 주세요">
      해지 시 정기결제 권한(빌링키)이 즉시 파기되며, 남은 이용기간에 대해 일할 계산하여 환불해 드립니다. 법정 보존 대상 기록은 분리 보관됩니다.
    </Alert>
    <Surface className="termination-card" title="개인정보 삭제 및 이용제한 안내">
      <ul className="termination-notices">
        <li>해지 즉시 서비스 이용이 제한되며, 등록하신 결제수단(빌링키)은 복구할 수 없습니다.</li>
        <li>{preview.personalDataHandlingNotice ?? "일반 회원정보는 지체 없이 파기됩니다. 미지급 환불에 필요한 정보와 법정 보존 기록만 별도로 보관합니다."}</li>
        <li>법령상 보존 의무가 있는 거래·정산 기록은 해당 보존기간 동안 분리 보관 후 파기됩니다.</li>
        <li>해지 후 30일 동안 같은 휴대폰 번호로 재가입할 수 없습니다. 이후 재가입해도 이전 이용내역·혜택은 복원되지 않습니다.</li>
      </ul>
      <ChoiceControl className="termination-consent" checked={agreed} disabled={pending || checkingQuote || !preview.allowed} onChange={event => setAgreed(event.currentTarget.checked)} label="위 안내를 확인했으며 개인정보 삭제 및 이용제한에 동의합니다."/>
    </Surface>
    {!preview.allowed ? <Alert persistent variant="warning" className="termination-condition" title="현재 해지할 수 없습니다"
      action={<Button variant="quiet" onClick={() => navigate("/app/transactions")}>거래 확인하기</Button>}>
      {preview.blockingReasonMessage ?? "진행 중인 거래를 먼저 완료해 주세요."}
      {preview.activeTransactionCount > 0 && <p>진행 중인 거래 {preview.activeTransactionCount}건</p>}
    </Alert> : <>
      {fullCancel ? <Alert persistent variant="information" className="termination-condition termination-condition--mint" title="결제 당일 해지">
        결제 당일 미사용 조건에 해당해 월정액 전액 취소 대상입니다. 해지 접수 후 기본 결제와 추가 결제를 각각 자동으로 취소 요청합니다.
      </Alert> : <Alert persistent variant="information" className="termination-condition termination-condition--mint" title={preview.customerTotalRefundAmount === 0 ? "환불 예정 금액이 없습니다" : "남은 이용기간에 대한 환불"}>
        {preview.customerTotalRefundAmount === 0 ? "이용금액을 차감한 환불 예정 금액은 0원입니다. 해지 접수 시 서비스 이용과 자동결제가 중단됩니다." : `${preview.serviceUsageDays != null ? `${preview.serviceUsageDays}일 ` : ""}이용금액을 차감한 금액을 환불합니다. 해지 접수와 실제 환불 완료는 별도 단계입니다.`}
      </Alert>}
      <Surface className="termination-card" title={fullCancel ? "취소 예정 금액" : "환불 예정 금액"}>
        <TerminationAmount label="취소(환불) 금액" amount={preview.customerTotalRefundAmount}/>
        {!fullCancel && <DetailList items={[
          { label: "전체 결제 금액", value: money(preview.grossAmount) },
          { label: "이용 일수", value: preview.serviceUsageDays == null ? "확인 중" : `${preview.serviceUsageDays}일` },
          { label: "이용 차감액", value: money(preview.serviceUsageDeduction) },
        ]}/>}
        {!fullCancel && preview.calculationText && <p className="termination-calculation">{preview.calculationText}</p>}
      </Surface>
      {needsAccount && <Surface className="termination-card" title="환불 계좌">
        {accountStatus === "loading" ? <p role="status" className="termination-help">은행과 회원 정보를 불러오는 중입니다.</p>
          : !accountReady ? <Alert persistent variant="warning" title="환불 계좌 정보를 준비하지 못했습니다"
            action={onRetryAccount && <Button variant="quiet" onClick={onRetryAccount}>다시 불러오기</Button>}>은행 목록과 회원 이름을 확인한 후 신청할 수 있습니다.</Alert>
          : <div className="termination-fields">
            <Field label="은행" control="select" required value={bankCode} disabled={pending} onChange={event => setBankCode(event.currentTarget.value)}><option value="">은행 선택</option>{banks.map(bank => <option key={bank.code} value={bank.code}>{bank.name}</option>)}</Field>
            <Field label="계좌번호" required inputMode="numeric" maxLength={20} autoComplete="off" value={accountNumber} disabled={pending} onChange={event => setAccountNumber(event.currentTarget.value.replace(/\D/g, ""))}/>
            <Field label="예금주" readOnly value={memberName} description="회원 본인 명의 계좌만 사용할 수 있습니다. 신청 시 예금주를 확인합니다."/>
          </div>}
      </Surface>}
    </>}
    {error && <Alert persistent variant="danger" title="해지 요청을 확인해 주세요" action={<Button variant="quiet" onClick={() => navigate("/termination/result")}>해지 결과 조회</Button>}>{error}<p>접수 후 응답을 받지 못했다면 본인확인으로 처리 상태를 조회할 수 있습니다.</p></Alert>}
    <div className="termination-actions">
      {guidance && <p id={guidanceId} className="termination-help">{guidance}</p>}
      <Button variant="danger" className="termination-danger-button" disabled={!ready || pending} isLoading={pending} loadingLabel="해지 접수 중" aria-describedby={guidance ? guidanceId : undefined} onClick={() => setConfirming(true)}>{label}</Button>
    </div>
    <Dialog className="termination-confirmation" open={confirming} title="회원 해지" description="해지 접수 시 서비스 이용이 종료되고 결제수단을 복구할 수 없습니다." tone="danger" onClose={() => setConfirming(false)} closeDisabled={pending}
      secondaryAction={<Button variant="secondary" disabled={pending} onClick={() => setConfirming(false)}>계속 이용하기</Button>}
      primaryAction={<Button variant="danger" className="termination-danger-button" disabled={!ready} isLoading={pending} loadingLabel="해지 접수 중" onClick={() => { if (ready && !pending && preview.quoteToken) onSubmit({ quoteToken: preview.quoteToken, reason: "회원 요청으로 서비스 해지", agreedToRefundPolicy: true, confirmed: true, ...(needsAccount ? { bankCode, accountNumber, accountHolder: memberName } : {}) }); }}>해지하기</Button>}>
      <TerminationAmount label={fullCancel ? "취소 예정 금액" : "환불 예정 금액"} amount={preview.customerTotalRefundAmount}/>
      <p className="termination-help">{fullCancel ? "해지 접수 직후 결제 취소를 자동으로 요청합니다. 결제별 처리 결과는 해지 결과 화면에서 확인해 주세요." : "해지가 접수되면 자동결제가 중단됩니다. 계좌 환불은 관리자가 확인 후 실행하며, 결과는 해지 결과 화면에서 확인할 수 있습니다."}</p>
      {error && <Alert persistent variant="danger" title="요청 실패">{error}</Alert>}
    </Dialog>
  </div>;
}
